export interface WireInfo {
  diameterIn: number; // wire diameter in inches
  lengthFt: number;   // length per coil in feet (not used directly)
  coilCost: number;   // cost per coil
  pricePerFt: number; // cost per foot
}

export const wireTable: WireInfo[] = [
  { diameterIn: 0.008, lengthFt: 1450, coilCost: 21.0, pricePerFt: 0.0145 },
  { diameterIn: 0.010, lengthFt: 935, coilCost: 18.71, pricePerFt: 0.0200 },
  { diameterIn: 0.014, lengthFt: 475, coilCost: 12.58, pricePerFt: 0.0265 },
  { diameterIn: 0.022, lengthFt: 190, coilCost: 7.26, pricePerFt: 0.0382 },
  { diameterIn: 0.051, lengthFt: 36, coilCost: 5.29, pricePerFt: 0.1469 },
];

/**
 * Find the closest matching wire price per foot for the provided diameter in inches.
 */
export function getPricePerFoot(diameterIn: number): number {
  let closest = wireTable[0];
  let minDiff = Math.abs(diameterIn - closest.diameterIn);

  for (const info of wireTable) {
    const diff = Math.abs(diameterIn - info.diameterIn);
    if (diff < minDiff) {
      closest = info;
      minDiff = diff;
    }
  }

  return closest.pricePerFt;
}

/**
 * Calculate the total wire length required for a spring configuration in feet.
 * All dimensions from the configuration should be in centimeters except for
 * thickness which is in millimeters.
 */
export interface SpringDimensions {
  diameter: number; // coil diameter in cm
  length: number;   // overall spring length in cm (not including hooks)
  coils: number;    // number of coils
  leftHook: boolean;
  rightHook: boolean;
  hookLength: number; // straight hook extension length in cm
}

export function calculateWireLengthFt(dim: SpringDimensions): number {
  const radius = dim.diameter / 2; // cm
  const pitch = dim.length / dim.coils; // cm per coil
  const circumference = 2 * Math.PI * radius; // cm per revolution
  const coilLength = Math.sqrt(circumference * circumference + pitch * pitch);
  let totalCm = coilLength * dim.coils;

  const hookArc = Math.PI * radius; // half circle
  if (dim.leftHook) totalCm += hookArc + dim.hookLength;
  if (dim.rightHook) totalCm += hookArc + dim.hookLength;

  return totalCm / 30.48; // convert cm -> ft
}

/**
 * Calculate the selling price for a spring given the dimensions and wire diameter.
 * Applies a 50% markup over the purchase cost of the wire.
 *
 * @param wireDiameterMm - wire thickness in millimeters
 * @param dimensions - spring dimensions
 */
export function calculateSpringPrice(
  wireDiameterMm: number,
  dimensions: SpringDimensions
): number {
  const diameterIn = wireDiameterMm / 25.4;
  const pricePerFt = getPricePerFoot(diameterIn);
  const wireLengthFt = calculateWireLengthFt(dimensions);
  const cost = wireLengthFt * pricePerFt;
  return cost * 1.5; // 50% margin
}
